import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import {
  SIDEBAR_MOBILE_BREAKPOINT,
  SIDEBAR_WIDTHS
} from '../constants/sidebarLayout';

export function useSidebarLayout() {
  const store = useStore();
  const isHovering = ref(false);
  const isUserMenuOpen = ref(false);
  const isMobile = ref(
    typeof window !== 'undefined' ? window.innerWidth < SIDEBAR_MOBILE_BREAKPOINT : false
  );

  let leaveTimer = null;

  const isCollapsed = computed(() => store.state.layout.sidebar.isCollapsed);
  const hoverExpandEnabled = computed(() => store.state.layout.sidebar.hoverExpandEnabled);
  const isReady = computed(() => store.state.layout.sidebar.isReady);

  const isHoverExpanded = computed(
    () =>
      !isMobile.value &&
      isCollapsed.value &&
      hoverExpandEnabled.value &&
      isHovering.value
  );

  const isVisuallyExpanded = computed(() => !isCollapsed.value || isHoverExpanded.value);

  const isRailMode = computed(
    () => !isMobile.value && isCollapsed.value && !isHoverExpanded.value
  );

  /**
   * Main column offset: rail when collapsed, full width when pinned open.
   * Hover-expand keeps rail offset — expanded sidebar overlays content temporarily.
   */
  const layoutWidth = computed(() => {
    if (isMobile.value) return '0px';
    if (!isCollapsed.value) return `${SIDEBAR_WIDTHS.expanded}px`;
    return `${SIDEBAR_WIDTHS.rail}px`;
  });

  /** Sidebar painted width — may exceed layoutWidth during hover overlay. */
  const visualWidth = computed(() => {
    if (isMobile.value) return `${SIDEBAR_WIDTHS.expanded}px`;
    const width = isVisuallyExpanded.value ? SIDEBAR_WIDTHS.expanded : SIDEBAR_WIDTHS.rail;
    return `${width}px`;
  });

  const collapseAriaExpanded = computed(() => !isCollapsed.value);

  const toggleCollapsed = () => {
    store.commit('layout/toggleSidebarCollapsed');
    isHovering.value = false;
  };

  const toggleHoverExpand = () => {
    store.commit('layout/toggleHoverExpandEnabled');
    if (!store.state.layout.sidebar.hoverExpandEnabled) {
      isHovering.value = false;
    }
  };

  const clearLeaveTimer = () => {
    if (leaveTimer) {
      clearTimeout(leaveTimer);
      leaveTimer = null;
    }
  };

  const handleMouseEnter = () => {
    if (isMobile.value) return;
    clearLeaveTimer();
    if (isCollapsed.value && hoverExpandEnabled.value) {
      isHovering.value = true;
    }
  };

  const handleMouseLeave = () => {
    if (isMobile.value || isUserMenuOpen.value) return;
    clearLeaveTimer();
    leaveTimer = setTimeout(() => {
      isHovering.value = false;
      leaveTimer = null;
    }, 180);
  };

  const setUserMenuOpen = (open) => {
    isUserMenuOpen.value = !!open;
    if (open) {
      clearLeaveTimer();
      if (!isMobile.value && isCollapsed.value && hoverExpandEnabled.value) {
        isHovering.value = true;
      }
    } else if (!isMobile.value && isCollapsed.value) {
      clearLeaveTimer();
      leaveTimer = setTimeout(() => {
        isHovering.value = false;
        leaveTimer = null;
      }, 180);
    }
  };

  const handleResize = () => {
    const mobile = window.innerWidth < SIDEBAR_MOBILE_BREAKPOINT;
    isMobile.value = mobile;
    store.commit('modals/setSidebarMobile', mobile);
    if (mobile) {
      isHovering.value = false;
      clearLeaveTimer();
    }
  };

  onMounted(() => {
    store.commit('layout/setSidebarReady', true);
    window.addEventListener('resize', handleResize);
    store.commit('modals/setSidebarMobile', isMobile.value);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    clearLeaveTimer();
  });

  return {
    isCollapsed,
    hoverExpandEnabled,
    isHovering,
    isMobile,
    isHoverExpanded,
    isVisuallyExpanded,
    isRailMode,
    isReady,
    layoutWidth,
    visualWidth,
    collapseAriaExpanded,
    toggleCollapsed,
    toggleHoverExpand,
    handleMouseEnter,
    handleMouseLeave,
    setUserMenuOpen
  };
}
